import { Slot } from '@radix-ui/react-slot';
import classNames from 'classnames';
import React, { useCallback, useState } from 'react';
import type { TicMeta } from 'types/tic';
import styles from './TicMetaIcon.module.scss';

export type TicMetaIconSize = 'small' | 'medium';

interface TicMetaIconProps extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  /** TIC metadata - renders nothing if missing or no icon */
  ticMeta?: TicMeta;
  /** Icon size variant */
  size?: TicMetaIconSize;
}

/**
 * Standardized TIC (Timely Integration Cloud) icon component.
 *
 * @description
 * Renders the tool icon from TIC metadata with consistent sizing and lazy loading.
 * Returns null if tic or tic.icon is missing, or if the image fails to load.
 *
 * @example
 * ```tsx
 * <TicMetaIcon ticMeta={project.tic} size="small" />
 * ```
 */
const TicMetaIcon = React.forwardRef<HTMLImageElement, TicMetaIconProps>(
  ({ ticMeta, size = 'medium', className, alt, ...rest }, ref) => {
    const [hasError, setHasError] = useState(false);

    const handleError = useCallback(() => {
      setHasError(true);
    }, []);

    if (!ticMeta?.icon || hasError) {
      return null;
    }

    return (
      <img
        ref={ref}
        src={ticMeta.icon}
        alt={alt ?? ticMeta.tool_id ?? 'Integration'}
        loading="lazy"
        decoding="async"
        onError={handleError}
        className={classNames(styles.icon, styles[`size-${size}`], className)}
        {...rest}
      />
    );
  }
);

TicMetaIcon.displayName = 'TicMetaIcon';

interface TicMetaIconOverlayProps extends React.HTMLAttributes<HTMLElement> {
  /** TIC metadata - renders only children if missing or no icon */
  ticMeta?: TicMeta;
  /** Icon size variant */
  size?: TicMetaIconSize;
  /** Child element to wrap */
  children: React.ReactNode;
  /**
   * Merges wrapper props onto immediate child instead of wrapping in span.
   * Child must accept className and style props.
   * @link https://www.radix-ui.com/primitives/docs/utilities/slot
   */
  asChild?: boolean;
}

/**
 * Wrapper component that overlays a TIC icon on its children.
 *
 * @description
 * Wraps children and positions a small TIC icon in the top-right corner.
 * If no TIC icon exists, renders children unwrapped.
 *
 * @example
 * ```tsx
 * // With wrapper span (default)
 * <TicMetaIcon.Overlay ticMeta={project.tic} size="small">
 *   <ProjectBadge color={color} />
 * </TicMetaIcon.Overlay>
 *
 * // With asChild - merges onto child
 * <TicMetaIcon.Overlay ticMeta={project.tic} asChild>
 *   <div className={styles.myWrapper}>
 *     <ProjectBadge color={color} />
 *   </div>
 * </TicMetaIcon.Overlay>
 * ```
 */
const TicMetaIconOverlay = React.forwardRef<HTMLElement, TicMetaIconOverlayProps>(
  ({ ticMeta, size = 'medium', children, className, asChild, ...rest }, ref) => {
    const [hasError, setHasError] = useState(false);

    const handleError = useCallback(() => {
      setHasError(true);
    }, []);

    // If no icon or error, render children without wrapper
    if (!ticMeta?.icon || hasError) {
      return <>{children}</>;
    }

    const Component = asChild ? Slot : 'span';

    return (
      <Component
        ref={ref as React.Ref<HTMLSpanElement>}
        className={classNames(styles.overlayWrapper, className)}
        {...rest}
      >
        {children}
        <img
          src={ticMeta.icon}
          alt={ticMeta.tool_id ?? 'Integration'}
          loading="lazy"
          decoding="async"
          onError={handleError}
          className={classNames(styles.icon, styles.overlay, styles[`size-${size}`])}
        />
      </Component>
    );
  }
);

TicMetaIconOverlay.displayName = 'TicMetaIcon.Overlay';

// Compound component pattern
const TicMetaIconWithOverlay = Object.assign(TicMetaIcon, {
  Overlay: TicMetaIconOverlay,
});

export default TicMetaIconWithOverlay;
export { TicMetaIconOverlay };
